import Pagination from "@mui/material/Pagination";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { usePopupStore, useSaleCompStore, useViewSettingsStore } from "../..";
import useActivePage from "../../hooks/useActivePage";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import usePagination from "../../hooks/usePagination";
import useSaleComps, { SaleCompsSortingType } from "../../hooks/useSaleComps";
import useSaleCompsOwnerFilterValues from "../../hooks/useSaleCompsOwnerFilterValues";
import useSaleCompsWithFocusArea from "../../hooks/useSaleCompsWithFocusArea";
import useSaleCompsWithGlobalFilters from "../../hooks/useSaleCompsWithGlobalFilters";
import PopupType from "../../interfaces/PopupType";
import { GlobalFiltersTab } from "../../store/FilterSettingsStore";
import { formatNumber } from "../../utils/utils";
import GlobalFiltersIcon from "../icons/GlobalFiltersIcon";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import SaleCompInfo from "./SaleCompInfo";

function SaleComps() {
    const { id } = useParams();
    const isSaleCompPage = useMatch("/saleComps/*");
    const saleCompStore = useSaleCompStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const navigateToSaleComp = useNavigateToSaleComp();
    const ownerFilterValues = useSaleCompsOwnerFilterValues();
    const [ownerFilter, setOwnerFilter] = useState(ownerFilterValues[0]);
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(SaleCompsSortingType.ModifiedNewest);
    const [page, setPage] = useState(1);
    const saleComps = useSaleCompsWithFocusArea(useSaleCompsWithGlobalFilters(useSaleComps(ownerFilter, debouncedSearchFilter, sortBy)));
    const [pageCount, pagedSaleComps] = usePagination(saleComps, page, () => setPage(1));
    useActivePage(saleComps, Number(id), isSaleCompPage && id && saleComps.find(sc => sc.id === Number(id)), searchFilter, ownerFilter, sortBy, setPage);

    useEffect(() => {
        if (!ownerFilterValues.includes(ownerFilter))
            setOwnerFilter(ownerFilterValues[0]);
    }, [ownerFilterValues, ownerFilter]);

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!saleCompStore.isSaleCompsLoading.fetching && formatNumber(saleComps.length)} Sale Comps
                {!viewSettingsStore.isMobile && <GlobalFiltersIcon tab={GlobalFiltersTab.SaleComps} className={tabStyles.headerButton} />}
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus={!viewSettingsStore.isMobile} placeholder="Search sale comps.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
                        {ownerFilterValues.map(v => <option key={v}>{v}</option>)}
                    </select>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={SaleCompsSortingType.ModifiedNewest}>Modified (Newest)</option>
                        <option value={SaleCompsSortingType.ModifiedOldest}>Modified (Oldest)</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {saleCompStore.isSaleCompsLoading.fetching ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedSaleComps.map(sc => (
                        <SaleCompInfo
                            key={sc.id}
                            className={[
                                tabStyles.item,
                                isSaleCompPage && id === sc.id.toString()
                                    ? tabStyles.opened
                                    : popupStore.type === PopupType.SaleComp && popupStore.id === sc.id
                                        ? tabStyles.selected
                                        : "",
                            ].join(" ")}
                            address={sc.address}
                            salePrice={sc.salePrice}
                            pipedriveBuyerId={sc.pipedriveBuyerId}
                            buildingSize={sc.buildingSize}
                            parcelSizeAcres={sc.parcelSizeAcres}
                            zoning={sc.zoning}
                            onClick={() => navigateToSaleComp(sc.id)}
                        />
                    ))
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    )
}

export default observer(SaleComps);